import { useEffect, useState } from "react";
import { lsClear, lsLoad, lsSave } from "../../tools/Storage";
import { reloadPage } from "../../tools/Routing";
import StorageEnum from "../../enums/Storage.enum";
import { toast } from "react-toastify";
import { toastOptions } from "../../tools/helpers/ToastHelper";
import canViewMarketComponentHistory from "../../security/CanViewMarketComponentHistory";
import { useTranslation } from "react-i18next";
import type {
  MarketComponentMarketComponentIO,
  MarketMarketIO,
} from "../../generated/fresh-guard-api";
import { useVersionSelect } from "../../apis/version/useVersionSelect.ts";
import { useWebSocket as useWebSocketProvider } from "../../provider/web-socket/WebSocket.hook.ts";
import { useNavigate } from "react-router";
import { useLogout } from "../../apis/logout/useLogout.ts";
import { extractMessageFromApi } from "../../tools/helpers/MessageHelper.ts";
import { useMarketComponentsForValidationSelect } from "../../apis/market-component/useMarketComponentsForValidationSelect.ts";

export const useMenuController = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const firstname = lsLoad(StorageEnum.UserProfile)?.firstname ?? "";
  const lastname = lsLoad(StorageEnum.UserProfile)?.lastname ?? "";
  const [isBurgerMenuOpen, setIsBurgerMenuOpen] = useState(false);
  const [isMarketSelectionOpen, setIsMarketSelectionOpen] = useState(false);
  const [isObjectSelectionOpen, setIsObjectSelectionOpen] = useState(false);
  const [isAreYouSureDialogOpen, setAreYouSureDialogOpen] = useState(false);
  const [isHistoryDialogOpen, setIsHistoryDialogOpen] = useState(false);
  const [isMarketPropertiesDialogOpen, setIsMarketPropertiesDialogOpen] =
    useState(false);
  const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(
    lsLoad(StorageEnum.Market)?.id?.toString() ?? "",
  );
  const [marketComponentForValidation, setMarketComponentForValidation] =
    useState<MarketComponentMarketComponentIO>();
  const { isWebSocketConnected } = useWebSocketProvider();

  const { refetch } = useMarketComponentsForValidationSelect(
    selectedValue,
    false,
  );
  const { data: backendVersion, isLoading: backendVersionLoading } =
    useVersionSelect();
  const { mutate, isPending } = useLogout();

  const handleHistoryClick = () => {
    if (canViewMarketComponentHistory()) {
      setIsHistoryDialogOpen(true);
    }
  };

  const handleMarketPropertiesClick = () => {
    setIsMarketPropertiesDialogOpen(true);
  };

  const handleAdministrationClick = () => {
    navigate("/administration");
  };

  const handleUserManagementClick = () => {
    navigate("/user-management");
  };

  const handleReportingClick = () => {
    navigate("/reporting");
  };

  const handleMarketManagementClick = () => {
    navigate("/dashboard");
  };

  const handleObjectSelectionClick = () => {
    setIsMarketSelectionOpen(false);
    setIsObjectSelectionOpen(!isObjectSelectionOpen);
  };

  const handleLogoutClick = () => {
    lsClear();
    reloadPage();
  };

  const handleRedRackClick = async () => {
    const result = await refetch();

    if (result.error) {
      toast.error(extractMessageFromApi(result.error, t).message, toastOptions);
      return;
    }

    const data = result.data;

    if (!data) return;

    if (!Array.isArray(data) || data.length === 0) {
      toast.success(t("ui.noRackForValidation"), toastOptions);
      return;
    }

    setMarketComponentForValidation(data[0]);
    setIsDetailsDialogOpen(true);
  };

  const handleMarketSelectionClick = () => {
    setIsObjectSelectionOpen(false);
    setIsMarketSelectionOpen(!isMarketSelectionOpen);
  };

  const handleMarketSelectionChanged = (market: MarketMarketIO) => {
    if (market && market.id) {
      const currentMarket = lsLoad(StorageEnum.Market);

      setSelectedValue(market.id.toString());
      setIsMarketSelectionOpen(false);
      lsSave(StorageEnum.Market, market);

      if (currentMarket?.id && currentMarket.id !== market.id) {
        setIsBurgerMenuOpen(false);
      }
    }
  };

  const handleHistoryDialogCloseClick = () => {
    setIsHistoryDialogOpen(false);
  };

  const handleMarketPropertiesDialogCloseClick = () => {
    setIsMarketPropertiesDialogOpen(false);
  };

  const handleDetailsDialogCloseClick = () => {
    setIsDetailsDialogOpen(false);
  };

  const handleLogoutAllClick = () => {
    setAreYouSureDialogOpen(true);
  };

  const handleLogoutYesClick = () => {
    mutate(undefined, {
      onSuccess: (data) => {
        toast.success(extractMessageFromApi(data, t).message, toastOptions);
        setAreYouSureDialogOpen(false);
      },
    });
  };

  const handleLogoutNoClick = () => {
    setAreYouSureDialogOpen(false);
  };

  useEffect(() => {
    if (typeof isWebSocketConnected === "boolean" && !isWebSocketConnected) {
      toast.error(t("error.webSocketNotConnected"), toastOptions);
    }
  }, [isWebSocketConnected, t]);

  return {
    firstname,
    lastname,
    handleHistoryClick,
    handleMarketPropertiesClick,
    handleAdministrationClick,
    handleMarketManagementClick,
    handleUserManagementClick,
    handleReportingClick,
    handleObjectSelectionClick,
    handleLogoutClick,
    handleMarketSelectionClick,
    handleMarketSelectionChanged,
    handleHistoryDialogCloseClick,
    handleMarketPropertiesDialogCloseClick,
    handleDetailsDialogCloseClick,
    handleRedRackClick,
    handleLogoutAllClick,
    handleLogoutYesClick,
    handleLogoutNoClick,
    setIsBurgerMenuOpen,
    isHistoryDialogOpen,
    isMarketPropertiesDialogOpen,
    isDetailsDialogOpen,
    loading: backendVersionLoading || isPending,
    marketComponentForValidation,
    isObjectSelectionOpen,
    isMarketSelectionOpen,
    isAreYouSureDialogOpen,
    selectedValue,
    backendVersion,
    isBurgerMenuOpen,
  };
};
